/**
 * InvoiceListPage.jsx
 *
 * Full invoice list with:
 *   - Stats cards (total / paid / pending / overdue)
 *   - Search + status filter
 *   - Table with all actions (view, PDF, payment, duplicate, cancel)
 *   - Create/Edit modal
 *   - Payment modal
 *   - PDF preview
 *
 * PASTE AT: frontend/src/pages/admin/accounting/InvoiceList.jsx  (NEW FILE)
 *
 * Then add route in App.jsx:
 *   import { InvoiceListPage } from './pages/admin/accounting/InvoiceList';
 *   <Route path="invoices/new"  element={<InvoiceListPage />} />
 */

import React, { useState, useEffect, useCallback } from 'react';
import { invoiceAPI } from '../../../services/api';
import { PageHeader }  from '../../../components/layout/PageHeader';
import { StatCard }    from '../../../components/common/StatCard';
import { Table }       from '../../../components/common/Table';
import { Button }      from '../../../components/common/Button';
import { Badge }       from '../../../components/common/Badge';
import { Modal }       from '../../../components/common/Modal';
import { Pagination }  from '../../../components/common/Pagination';
import { InvoiceForm } from './InvoiceForm';
import { InvoiceDownloadModal } from './InvoicePDF';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  PlusIcon, MagnifyingGlassIcon, EyeIcon, ArrowDownTrayIcon,
  DocumentDuplicateIcon, BanknotesIcon, TrashIcon,
  ArrowsRightLeftIcon, PencilIcon, PrinterIcon,
} from '@heroicons/react/24/outline';

const STATUS_COLORS = {
  DRAFT:          'bg-gray-100 text-gray-600',
  PENDING:        'bg-amber-100 text-amber-700',
  PARTIALLY_PAID: 'bg-blue-100 text-blue-700',
  PAID:           'bg-green-100 text-green-700',
  OVERDUE:        'bg-red-100 text-red-700',
  CANCELLED:      'bg-red-50 text-red-400',
  VOIDED:         'bg-gray-50 text-gray-400',
  REFUNDED:       'bg-purple-100 text-purple-700',
};

const TYPE_LABELS = {
  INVOICE: 'Tax Invoice', QUOTE: 'Quotation', PROFORMA: 'Proforma',
  SALES_ORDER: 'Sales Order', DELIVERY_CHALLAN: 'Delivery Challan',
  CREDIT_NOTE: 'Credit Note', DEBIT_NOTE: 'Debit Note',
};

const CONVERT_OPTIONS = {
  QUOTE:            [{ value: 'INVOICE', label: '→ Tax Invoice' }, { value: 'PROFORMA', label: '→ Proforma' }],
  PROFORMA:         [{ value: 'INVOICE', label: '→ Tax Invoice' }, { value: 'DELIVERY_CHALLAN', label: '→ Delivery Challan' }],
  INVOICE:          [{ value: 'DELIVERY_CHALLAN', label: '→ Delivery Challan' }, { value: 'CREDIT_NOTE', label: '→ Credit Note' }],
  SALES_ORDER:      [{ value: 'INVOICE', label: '→ Tax Invoice' }, { value: 'DELIVERY_CHALLAN', label: '→ Delivery Challan' }],
  DELIVERY_CHALLAN: [{ value: 'INVOICE', label: '→ Tax Invoice' }],
};

// Which type this page is showing
export function InvoiceListPage({ docType = 'INVOICE' }) {
  const [records, setRecords]   = useState([]);
  const [pg, setPg]             = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [stats, setStats]       = useState({ total: 0, paid: 0, pending: 0, overdue: 0 });

  // Modal states
  const [formOpen, setFormOpen]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [viewing, setViewing]       = useState(null);
  const [viewOpen, setViewOpen]     = useState(false);
  const [payRec, setPayRec]         = useState(null);
  const [payOpen, setPayOpen]       = useState(false);
  const [convertRec, setConvertRec] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [printRec, setPrintRec]     = useState(null);
  const [printOpen, setPrintOpen]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoiceAPI.getAll({
        type: docType, status: status || undefined,
        search: search || undefined, page, pageSize: 15,
      });
      setRecords(res.data || []);
      setPg({ total: res.total, page: res.page, limit: res.pageSize });

      // Compute stats from results
      const data = res.data || [];
      setStats({
        total:   res.total || 0,
        paid:    data.filter(r => r.status === 'PAID').reduce((s, r) => s + Number(r.grandTotal || 0), 0),
        pending: data.filter(r => ['PENDING', 'PARTIALLY_PAID'].includes(r.status)).reduce((s, r) => s + Number(r.dueAmount || 0), 0),
        overdue: data.filter(r => r.status === 'OVERDUE').length,
      });
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [docType, status, search, page]);

  useEffect(() => { load(); }, [load]);

  // ── Actions ───────────────────────────────────────────────

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit   = r  => { setEditing(r);    setFormOpen(true); };

  const openView = async r => {
    try {
      const res = await invoiceAPI.getById(r._id);
      setPrintRec(res.data);
      setPrintOpen(true);
    } catch { toast.error('Failed to load'); }
  };

  const openPdf = async (r) => {
    try {
      const token = localStorage.getItem('token');
      // Quotation/Proforma use their own premium design automatically on backend
      // Invoice/others use classic template
      const url = `/api/invoices/${r._id}/pdf`;
      const res = await fetch(url, {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) { toast.error('Failed to generate PDF'); return; }
      const blob    = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a       = document.createElement('a');
      a.href        = blobUrl;
      a.download    = `${r.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch { toast.error('PDF download failed'); }
  };

  const openPrint = async r => {
    try {
      const res = await invoiceAPI.getById(r._id);
      setPrintRec(res.data);
      setPrintOpen(true);
    } catch { toast.error('Failed to load for print'); }
  };

  const handleDuplicate = async r => {
    try {
      await invoiceAPI.duplicate(r._id);
      toast.success('Duplicated as draft');
      load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const handleCancel = async r => {
    if (!window.confirm('Cancel this document?')) return;
    try {
      await invoiceAPI.cancel(r._id, 'Cancelled by user');
      toast.success('Cancelled');
      load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const handleDelete = async r => {
    if (!window.confirm(`Delete ${r.invoiceNumber || 'this record'}?`)) return;
    try {
      await invoiceAPI.delete(r._id);
      toast.success('Deleted successfully');
      load();
    } catch (e) { toast.error(e.message || 'Failed to delete'); }
  };

  const handleConvert = async targetType => {
    if (!convertRec) return;
    setConverting(true);
    try {
      await invoiceAPI.convert(convertRec._id, targetType);
      toast.success(`Converted to ${TYPE_LABELS[targetType]}`);
      setConvertOpen(false);
      load();
    } catch (e) { toast.error(e.message || 'Conversion failed'); }
    finally { setConverting(false); }
  };

  const convertOptions = CONVERT_OPTIONS[docType] || [];

  // ── Page title ────────────────────────────────────────────

  const pageTitle = {
    INVOICE:          'Invoices',
    QUOTE:            'Quotations',
    PROFORMA:         'Proforma Invoices',
    SALES_ORDER:      'Sales Orders',
    DELIVERY_CHALLAN: 'Delivery Challans',
    CREDIT_NOTE:      'Credit Notes',
    DEBIT_NOTE:       'Debit Notes',
  }[docType] || 'Documents';

  // ── Table columns ─────────────────────────────────────────

  const cols = [
    {
      header: 'Number',
      render: r => (
        <button onClick={() => openView(r)} className="font-mono text-xs font-semibold text-indigo-600 hover:underline">
          {r.invoiceNumber}
        </button>
      ),
    },
    {
      header: 'Date',
      render: r => (
        <div>
          <p className="text-sm text-gray-800">{r.invoiceDate ? format(new Date(r.invoiceDate), 'dd MMM yyyy') : '—'}</p>
          {r.dueDate && <p className="text-xs text-gray-400">Due {format(new Date(r.dueDate), 'dd MMM')}</p>}
        </div>
      ),
    },
    {
      header: 'Customer',
      render: r => (
        <div>
          <p className="text-sm font-medium text-gray-900">{r.customerId?.name || '—'}</p>
          {r.customerId?.phone && <p className="text-xs text-gray-400">{r.customerId.phone}</p>}
        </div>
      ),
    },
    {
      header: 'Amount',
      render: r => (
        <div>
          <p className="font-bold text-gray-900 text-sm">₹{Number(r.grandTotal || 0).toLocaleString('en-IN')}</p>
          {r.paidAmount > 0 && r.dueAmount > 0 && (
            <p className="text-xs text-amber-600">Due ₹{Number(r.dueAmount).toLocaleString('en-IN')}</p>
          )}
          {r.status === 'PAID' && <p className="text-xs text-green-600">Fully paid</p>}
        </div>
      ),
    },
    {
      header: 'GST',
      render: r => (
        <div className="text-xs text-gray-500">
          {r.isIgst
            ? <span>IGST ₹{Number(r.igstAmount || 0).toLocaleString('en-IN')}</span>
            : <span>CGST+SGST ₹{Number((r.cgstAmount || 0) + (r.sgstAmount || 0)).toLocaleString('en-IN')}</span>}
        </div>
      ),
    },
    {
      header: 'Status',
      render: r => (
        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-600'}`}>
          {r.status?.replace('_', ' ')}
        </span>
      ),
    },
    {
      header: '',
      render: r => (
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => openView(r)} title="View" className="p-1.5 hover:bg-blue-50 rounded-lg">
            <EyeIcon className="w-4 h-4 text-blue-500" />
          </button>
          <button onClick={() => openPdf(r)} title="Download PDF" className="p-1.5 hover:bg-green-50 rounded-lg">
            <ArrowDownTrayIcon className="w-4 h-4 text-green-600" />
          </button>
          <button onClick={() => openPrint(r)} title="Print" className="p-1.5 hover:bg-orange-50 rounded-lg">
            <PrinterIcon className="w-4 h-4 text-orange-500" />
          </button>
          {['PENDING', 'PARTIALLY_PAID', 'OVERDUE'].includes(r.status) && (
            <button onClick={() => { setPayRec(r); setPayOpen(true); }} title="Record Payment" className="p-1.5 hover:bg-emerald-50 rounded-lg">
              <BanknotesIcon className="w-4 h-4 text-emerald-600" />
            </button>
          )}
          <button onClick={() => handleDuplicate(r)} title="Duplicate" className="p-1.5 hover:bg-violet-50 rounded-lg">
            <DocumentDuplicateIcon className="w-4 h-4 text-violet-600" />
          </button>
          {convertOptions.length > 0 && !r.convertedToId && (
            <button onClick={() => { setConvertRec(r); setConvertOpen(true); }} title="Convert" className="p-1.5 hover:bg-orange-50 rounded-lg">
              <ArrowsRightLeftIcon className="w-4 h-4 text-orange-500" />
            </button>
          )}
          {['DRAFT', 'PENDING'].includes(r.status) && (
            <button onClick={() => openEdit(r)} title="Edit" className="p-1.5 hover:bg-indigo-50 rounded-lg">
              <PencilIcon className="w-4 h-4 text-indigo-600" />
            </button>
          )}
          <button onClick={() => handleDelete(r)} title="Delete" className="p-1.5 hover:bg-red-50 rounded-lg">
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      <PageHeader title={pageTitle} subtitle={`Manage all ${pageTitle.toLowerCase()}`} />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total"   value={loading ? '...' : stats.total}                                          color="indigo" />
        <StatCard title="Paid"    value={loading ? '...' : `₹${(stats.paid / 1000).toFixed(0)}K`}               color="green"  />
        <StatCard title="Pending" value={loading ? '...' : `₹${(stats.pending / 1000).toFixed(0)}K`}            color="amber"  />
        <StatCard title="Overdue" value={loading ? '...' : stats.overdue} subtitle="invoices"                   color="red"    />
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-100 flex gap-3 flex-wrap items-center justify-between">
          <div className="flex gap-3 flex-1 flex-wrap">
            <div className="relative flex-1 min-w-44">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${pageTitle.toLowerCase()}...`}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">All Status</option>
              {['DRAFT','PENDING','PARTIALLY_PAID','PAID','OVERDUE','CANCELLED','VOIDED'].map(s => (
                <option key={s} value={s}>{s.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <Button icon={PlusIcon} onClick={openCreate}>New {pageTitle.replace(/s$/, '')}</Button>
        </div>

        <Table columns={cols} data={records} loading={loading} emptyMessage={`No ${pageTitle.toLowerCase()} found`} />
        <Pagination pagination={pg} onPageChange={setPage} />
      </div>

      {/* Create / Edit form */}
      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title={editing ? `Edit ${pageTitle.replace(/s$/, '')}` : `New ${pageTitle.replace(/s$/, '')}`} size="xl">
        <InvoiceForm
          editing={editing}
          docType={docType}
          onSave={() => { setFormOpen(false); load(); }}
          onClose={() => setFormOpen(false)}
        />
      </Modal>

      {/* View detail */}
      {viewing && (
        <Modal isOpen={viewOpen} onClose={() => setViewOpen(false)} title={viewing.invoiceNumber} size="lg">
          <InvoiceDetailView
            invoice={viewing}
            onClose={() => setViewOpen(false)}
            onPdf={() => openPdf(viewing)}
            onPrint={() => openPrint(viewing)}
            onPayment={() => { setPayRec(viewing); setViewOpen(false); setPayOpen(true); }}
          />
        </Modal>
      )}

      {/* Payment modal */}
      <PaymentModal
        isOpen={payOpen}
        onClose={() => setPayOpen(false)}
        record={payRec}
        onSave={async data => {
          await invoiceAPI.addPayment(payRec._id, data);
          toast.success('Payment recorded');
          setPayOpen(false);
          load();
        }}
      />

      {/* Convert modal */}
      <Modal isOpen={convertOpen} onClose={() => setConvertOpen(false)} title="Convert Document" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Convert <span className="font-mono font-semibold">{convertRec?.invoiceNumber}</span> to:</p>
          <div className="space-y-2">
            {convertOptions.map(opt => (
              <button key={opt.value} onClick={() => handleConvert(opt.value)} disabled={converting}
                className="w-full text-left px-4 py-3 rounded-xl border-2 border-violet-200 hover:border-violet-500 hover:bg-violet-50 text-sm font-semibold text-violet-700 disabled:opacity-50">
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400">Original document is marked as converted. New document created as draft.</p>
        </div>
      </Modal>

      {/* Print / Preview Modal — same inline format as accounting records */}
      {printRec && (
        <InvoiceDownloadModal
          isOpen={printOpen}
          onClose={() => { setPrintOpen(false); setPrintRec(null); }}
          record={printRec}
        />
      )}

    </div>
  );
}

// ── Invoice Detail View ───────────────────────────────────────

function InvoiceDetailView({ invoice: inv, onClose, onPdf, onPrint, onPayment }) {
  const balance = Number(inv.grandTotal || 0) - Number(inv.paidAmount || 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start bg-indigo-50 rounded-2xl p-4">
        <div>
          <p className="text-xl font-bold font-mono text-gray-900">{inv.invoiceNumber}</p>
          <p className="text-sm text-gray-500 mt-0.5">{inv.invoiceDate ? format(new Date(inv.invoiceDate), 'dd MMMM yyyy') : '—'}</p>
          {inv.placeOfSupply && <p className="text-xs text-gray-400 mt-1">Place of Supply: {inv.placeOfSupply}</p>}
        </div>
        <div className="text-right space-y-1">
          <p className="text-2xl font-bold text-indigo-600">₹{Number(inv.grandTotal || 0).toLocaleString('en-IN')}</p>
          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[inv.status] || ''}`}>
            {inv.status?.replace('_', ' ')}
          </span>
          <div className="flex gap-2 mt-2">
            <button onClick={() => onPdf()}
              className="text-xs text-green-600 hover:underline flex items-center gap-1">
              <ArrowDownTrayIcon className="w-3 h-3" /> Download PDF
            </button>
            <button onClick={() => onPrint()}
              className="text-xs text-orange-500 hover:underline flex items-center gap-1">
              <PrinterIcon className="w-3 h-3" /> Print
            </button>
            {['PENDING', 'PARTIALLY_PAID', 'OVERDUE'].includes(inv.status) && (
              <button onClick={onPayment}
                className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                <BanknotesIcon className="w-3 h-3" /> Record Payment
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Customer */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Customer</p>
          <p className="font-semibold text-gray-900">{inv.customerId?.name || inv.billingAddress?.name || '—'}</p>
          {inv.customerId?.gstin && <p className="font-mono text-xs text-gray-400">GSTIN: {inv.customerId.gstin}</p>}
          {inv.billingAddress?.address && <p className="text-xs text-gray-500 mt-0.5">{inv.billingAddress.address}</p>}
        </div>
        <div className="space-y-1 text-xs">
          {inv.dueDate     && <div className="flex justify-between"><span className="text-gray-400">Due Date</span><span className="font-medium">{format(new Date(inv.dueDate), 'dd MMM yyyy')}</span></div>}
          {inv.poNumber    && <div className="flex justify-between"><span className="text-gray-400">PO/WO No.</span><span className="font-medium">{inv.poNumber}</span></div>}
          {inv.reverseCharge && <div className="flex justify-between"><span className="text-gray-400">Reverse Charge</span><span className="font-medium text-amber-600">Yes</span></div>}
        </div>
      </div>

      {/* Items */}
      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['#', 'Item', 'HSN', 'Qty', 'Rate', 'Tax', 'Total'].map(h => (
                <th key={h} className={`text-xs font-semibold text-gray-400 px-3 py-2.5 ${h === '#' || h === 'Item' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(inv.items || []).map((item, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                <td className="px-3 py-2.5">
                  <p className="font-medium text-gray-900">{item.name}</p>
                  {item.description && <p className="text-xs text-gray-400">{item.description}</p>}
                </td>
                <td className="px-3 py-2.5 text-right text-xs text-gray-400">{item.hsnCode || '—'}</td>
                <td className="px-3 py-2.5 text-right text-gray-600">{item.quantity} {item.unit}</td>
                <td className="px-3 py-2.5 text-right text-gray-600">₹{Number(item.rate || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2.5 text-right text-xs text-gray-500">
                  {inv.isIgst
                    ? `IGST ${item.igstRate}%`
                    : `${item.cgstRate}%+${item.sgstRate}%`}
                </td>
                <td className="px-3 py-2.5 text-right font-semibold text-gray-900">₹{Number(item.lineTotal || 0).toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 flex justify-end">
          <div className="w-64 space-y-1 text-sm">
            <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>₹{Number(inv.subtotal || 0).toLocaleString('en-IN')}</span></div>
            {inv.discountAmount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>−₹{Number(inv.discountAmount).toLocaleString('en-IN')}</span></div>}
            {inv.isIgst
              ? <div className="flex justify-between text-gray-500"><span>IGST</span><span>₹{Number(inv.igstAmount || 0).toLocaleString('en-IN')}</span></div>
              : <>
                  <div className="flex justify-between text-gray-500"><span>CGST</span><span>₹{Number(inv.cgstAmount || 0).toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between text-gray-500"><span>SGST</span><span>₹{Number(inv.sgstAmount || 0).toLocaleString('en-IN')}</span></div>
                </>
            }
            {inv.roundOff !== 0 && <div className="flex justify-between text-gray-400"><span>Round Off</span><span>₹{Number(inv.roundOff || 0).toFixed(2)}</span></div>}
            <div className="flex justify-between font-bold text-base text-gray-900 border-t border-gray-300 pt-1.5">
              <span>Total</span><span className="text-indigo-600">₹{Number(inv.grandTotal || 0).toLocaleString('en-IN')}</span>
            </div>
            {inv.paidAmount > 0 && <>
              <div className="flex justify-between text-green-600"><span>Paid</span><span>₹{Number(inv.paidAmount).toLocaleString('en-IN')}</span></div>
              <div className={`flex justify-between font-semibold ${balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                <span>Balance Due</span><span>₹{balance.toLocaleString('en-IN')}</span>
              </div>
            </>}
          </div>
        </div>
      </div>

      {/* Payment history */}
      {inv.payments?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Payment History</p>
          <div className="space-y-1">
            {inv.payments.map((p, i) => (
              <div key={i} className="flex justify-between items-center bg-green-50 rounded-xl px-3 py-2 text-sm">
                <span className="text-gray-600">{p.paymentDate ? format(new Date(p.paymentDate), 'dd MMM yyyy') : '—'} · {p.paymentMethod}</span>
                <span className="font-semibold text-green-700">₹{Number(p.amount).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {inv.notes && (
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
          <p className="text-xs font-semibold text-amber-600 mb-1">Notes</p>
          <p className="text-sm text-gray-700">{inv.notes}</p>
        </div>
      )}
    </div>
  );
}

// ── Payment Modal ─────────────────────────────────────────────

function PaymentModal({ isOpen, onClose, record, onSave }) {
  const [form, setForm]   = useState({ paymentMethod: 'CASH', amount: '', paymentDate: new Date().toISOString().split('T')[0], reference: '' });
  const [saving, setSaving] = useState(false);

  const balance = record ? Number(record.grandTotal || 0) - Number(record.paidAmount || 0) : 0;

  const submit = async e => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (Number(form.amount) > balance)            { toast.error('Amount exceeds balance due'); return; }
    setSaving(true);
    try { await onSave({ ...form, amount: Number(form.amount) }); }
    catch (err) { toast.error(err.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Record Payment" size="sm">
      {record && (
        <form onSubmit={submit} className="space-y-4">
          <div className="bg-indigo-50 rounded-xl p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Invoice Total</span><span className="font-bold">₹{Number(record.grandTotal || 0).toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Already Paid</span><span className="text-green-600 font-semibold">₹{Number(record.paidAmount || 0).toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between border-t border-indigo-200 pt-1"><span className="font-semibold text-gray-700">Balance Due</span><span className="font-bold text-amber-600">₹{balance.toLocaleString('en-IN')}</span></div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Amount (₹)</label>
              <input type="number" min="1" max={balance} step="0.01" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder={`Max ₹${balance}`} className={inp} required />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Payment Method</label>
              <select value={form.paymentMethod} onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))} className={inp}>
                {['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'WALLET'].map(m => (
                  <option key={m} value={m}>{m.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Date</label>
              <input type="date" value={form.paymentDate} onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))} className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Reference / UTR</label>
              <input type="text" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Cheque no / UTR / Transaction ID" className={inp} />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-indigo-600 rounded-xl text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
